import { useState, useCallback, useMemo, memo } from 'react';
import { createPortal } from 'react-dom';
import { X, RefreshCw, RotateCcw, CheckCircle2, XCircle } from 'lucide-react';
import RichTextBlockEleve from './RichTextBlockEleve';
import QuestionInteractive from './interactifs/QuestionInteractive';
import { QuizThemeContext } from './interactifs/quizTheme';
import TableResponseElementEleve from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/TableResponseElementEleve';
import CoupDePouceButton from '../../pages/EspaceEleve/CoupDePouceButton';
import CalculatriceCollege from '../../pages/EspaceEleve/CalculatriceCollege';
import { getIndice } from '../../utils/coupDePouce';
import {
    getTableElementConfig,
    resolveBonneReponse,
    compareAnswers,
    compareComplexAnswers,
    isComplexAnswer,
    optionsDeQuestion,
    calculatriceAutorisee,
    calculatriceCosinus,
    comparaisonExpression,
} from '../../utils/questionUtils';

// Le FAB doit passer AU-DESSUS de l'overlay du modal (z-[10010]).
const CP_Z_INDEX = 10020;

/**
 * Corps de l'aperçu élève INTERACTIF : l'énoncé + la zone de réponse jouable
 * (les mêmes composants que la passation réelle, thème « clair »), un bouton
 * « Valider » qui score la réponse avec les VRAIES fonctions de scoring
 * (resolveBonneReponse + compareAnswers/compareComplexAnswers, cf. Quiz.jsx et
 * le simulateur QA), puis un feedback ✓/✗ ; si c'est faux, le widget se marque
 * lui-même (correction sur place), le prof voit ce que l'élève verra.
 *
 * Monté sous une `key` de génération par le modal → tout changement de variante
 * force un remontage complet (contrat des interactifs : selectedValue lu une
 * seule fois au montage).
 *
 * @param {Object} question - payload unifié { type|type_question, texte, contenu,
 *   elements_additionnels, bonne_reponse?, image? }
 * @param {Function|null} onVariant - regénère une autre variante (ou null)
 */
const ApercuEleveBody = memo(function ApercuEleveBody({ question, onVariant }) {
    // Réponse courante émise par le composant interactif (string / JSON / complexe)
    const [answer, setAnswer] = useState(null);
    // Verdict après « Valider » : null (pas encore) | true (juste) | false (faux)
    const [verdict, setVerdict] = useState(null);
    // Clé de remontage de la zone interactive pour « Réessayer » (reset d'état)
    const [attempt, setAttempt] = useState(0);
    // Coup de pouce : ouverture + « déjà consulté » (aucun XP en jeu ici, c'est
    // l'enseignant qui relit son aide ; on garde l'état pour le rendu du FAB).
    const [aideOuverte, setAideOuverte] = useState(false);
    const [aideVue, setAideVue] = useState(false);

    const typeQuestion = question.type_question || question.type || 'qcm';
    // Mémoïsé : `question.contenu || {}` recréait un objet à chaque render, ce qui
    // invalidait les useMemo en aval (correct, tableConfig) sans raison.
    const contenu = useMemo(() => question.contenu || {}, [question.contenu]);
    const elements = question.elements_additionnels?.elements || [];

    // Tableau (Vrai/Faux…) : détecté depuis elements_additionnels OU bonne_reponse
    // au format [phrase|valeur] — mêmes règles que ExercicePreview / Quiz.jsx.
    const tableConfig = useMemo(() => getTableElementConfig({
        ...question,
        bonne_reponse: question.bonne_reponse || contenu.bonne_reponse,
    }) || null, [question, contenu.bonne_reponse]);

    // Le tableau est rendu séparément dans la zone réponse → on l'ôte de l'énoncé.
    const enonceElements = tableConfig ? elements.filter(el => el.type !== 'table') : elements;

    // Bonne réponse RÉSOLUE (QCM : lettre → contenu ; autres : contenu direct).
    const correct = useMemo(() => resolveBonneReponse(contenu), [contenu]);

    const answered = answer != null && answer !== '';
    const locked = verdict !== null;
    // Correction SUR PLACE (2026-09-21) : le prof voit exactement ce que l'élève
    // verra. Validé et faux : le widget déjà à l'écran reçoit la correction et
    // marque ses éléments ; l'encadré du bas garde le verdict, rien d'autre.
    const surPlace = locked && verdict === false;
    const corrige = useMemo(() => (surPlace ? { bonneReponse: correct, reponseEleve: answer ?? '', options: optionsDeQuestion(question) } : null), [surPlace, correct, answer, question]);

    const handleAnswer = useCallback((value) => setAnswer(value), []);

    // Scoring identique à la passation (Quiz.jsx) : format complexe → comparaison
    // complexe, sinon comparaison intelligente (JSON / trous / normalisée).
    const handleValidate = useCallback(() => {
        const sa = answer || '';
        const ok = isComplexAnswer(sa) && isComplexAnswer(correct)
            ? compareComplexAnswers(sa, correct)
            : compareAnswers(sa, correct, question.bonne_reponse_image, optionsDeQuestion(question));
        setVerdict(ok);
    }, [answer, correct, question]);

    const handleRetry = useCallback(() => {
        setAnswer(null);
        setVerdict(null);
        setAttempt((a) => a + 1);
    }, []);

    // Le FAB n'est monté QUE si un coup de pouce existe pour ce format : côté
    // prof, un bouton d'aide vide n'aurait aucun sens (contrairement à la
    // passation, où Tano sert toujours un conseil générique).
    const indice = useMemo(() => getIndice(question), [question]);
    const handleAideOpen = useCallback(() => {
        setAideOuverte(true);
        setAideVue(true);
    }, []);
    const handleAideClose = useCallback(() => setAideOuverte(false), []);

    return (
        <div className="space-y-4">
            {/* ── Carte question BLANCHE (style passation) ── */}
            <div className="bg-white rounded-2xl shadow-xl shadow-black/20 p-4 sm:p-6">
                <QuizThemeContext.Provider value="clair">
                    {tableConfig ? (
                        <div className="w-full">
                            {question.texte && (
                                <div className="mb-4 text-base sm:text-lg">
                                    <RichTextBlockEleve texte={question.texte} elements={enonceElements} theme="light" />
                                </div>
                            )}
                            {/* Remonté à chaque tentative → reset des cases. Sur place, le
                                tableau se marque lui-même : pas de grisé, sinon ses couleurs s'éteignent. */}
                            <div className={locked && !surPlace ? 'pointer-events-none opacity-70' : ''}>
                                <TableResponseElementEleve
                                    key={attempt}
                                    config={tableConfig}
                                    light
                                    onAnswerChange={(formattedAnswer) => {
                                        if (formattedAnswer && formattedAnswer !== '') handleAnswer(formattedAnswer);
                                    }}
                                    reponseExistante={answer || ''}
                                    corrige={corrige}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4 w-full">
                            {/* Énoncé : image (prof) OU texte enrichi */}
                            {question.image ? (
                                <img src={question.image} alt="Énoncé" className="max-w-full max-h-72 rounded-lg mb-1 mx-auto object-contain" />
                            ) : (
                                <div className="text-lg sm:text-xl text-center" style={{ color: '#0A1F3D' }}>
                                    <RichTextBlockEleve texte={question.texte} elements={enonceElements} theme="light" />
                                </div>
                            )}

                            {/* Zone de réponse jouable — remontée à chaque tentative */}
                            {/* Sur place, le widget se marque lui-même et passe en lecture
                                seule : pas de grisé, sinon les couleurs de correction s'éteignent. */}
                            <div className={locked && !surPlace ? 'pointer-events-none opacity-70' : ''}>
                                <QuestionInteractive
                                    key={attempt}
                                    typeQuestion={typeQuestion}
                                    contenu={contenu}
                                    selectedValue={answer}
                                    onAnswer={handleAnswer}
                                    onComplete={() => {}}
                                    onAbandon={() => {}}
                                    radioName={`apercu-${attempt}`}
                                    corrige={corrige}
                                    expression={comparaisonExpression(question)}
                                />
                            </div>
                        </div>
                    )}
                </QuizThemeContext.Provider>
            </div>

            {/* ── Feedback après validation ── */}
            {locked && (
                <div className={`rounded-2xl border p-4 ${verdict
                    ? 'bg-emerald-50 border-emerald-300'
                    : 'bg-rose-50 border-rose-300'}`}>
                    <div className={`flex items-center gap-2 font-bold ${verdict ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {verdict ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
                        {verdict ? 'Bravo, bonne réponse !' : 'Réponse incorrecte'}
                    </div>
                </div>
            )}

            {/* ── Actions ── */}
            <div className="flex items-center gap-3 flex-wrap">
                {!locked ? (
                    <button
                        onClick={handleValidate}
                        disabled={!answered}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition ${answered
                            ? 'bg-blue-600 hover:bg-blue-700'
                            : 'bg-slate-300 cursor-not-allowed'}`}
                    >
                        Valider
                    </button>
                ) : (
                    <button
                        onClick={handleRetry}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-200 border border-slate-500 hover:bg-slate-700 transition"
                    >
                        <RotateCcw size={14} /> Réessayer
                    </button>
                )}
                {onVariant && (
                    <button
                        onClick={onVariant}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 border border-slate-600 hover:bg-slate-700 hover:text-slate-100 transition"
                    >
                        <RefreshCw size={14} /> Autre variante
                    </button>
                )}
            </div>

            {/* ── Coup de pouce : même FAB + même sheet qu'en passation ── */}
            {indice && (
                <CoupDePouceButton
                    question={question}
                    open={aideOuverte}
                    used={aideVue}
                    onOpen={handleAideOpen}
                    onClose={handleAideClose}
                    zIndex={CP_Z_INDEX}
                    showXp={false}
                />
            )}

            {/* ── Calculatrice : montée sur le seul flag contenu.calculatrice
                (le prof voit exactement l'outillage de l'élève) — rangée à
                droite du FAB d'aide quand il est là ── */}
            {calculatriceAutorisee(question) ? <CalculatriceCollege zIndex={CP_Z_INDEX} decale={!!indice} cosinus={calculatriceCosinus(question)} /> : null}
        </div>
    );
});

/**
 * Modal d'aperçu élève INTERACTIF (tester les interactions + valider + voir le
 * rendu). Remplace, dans l'exerciseur, l'aperçu en lecture seule : l'enseignant
 * joue l'exercice comme un élève.
 *
 * @param {Object} question - payload unifié (voir ApercuEleveBody)
 * @param {Function} onClose - ferme le modal
 * @param {Function} [regenerate] - () => nouveau payload (même attendu/format) ;
 *   fourni par l'exerciseur pour le bouton « Autre variante ». Optionnel.
 */
const ApercuEleveModal = ({ question, onClose, regenerate }) => {
    const [q, setQ] = useState(question);
    const [gen, setGen] = useState(0);

    const handleVariant = useCallback(() => {
        if (!regenerate) return;
        try {
            const next = regenerate();
            if (next) {
                setQ(next);
                setGen((g) => g + 1);
            }
        } catch { /* ignore */ }
    }, [regenerate]);

    if (!q) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[10010] bg-slate-950/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
            onClick={onClose}
        >
            <div
                className="w-full max-w-2xl max-h-[90vh] bg-slate-800 rounded-2xl flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 flex-shrink-0">
                    <div>
                        <h3 className="text-lg font-bold text-white">Aperçu élève</h3>
                        <p className="text-[11px] text-slate-400">Teste les interactions et valide comme un élève</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center text-white transition"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Corps interactif scrollable */}
                <div className="flex-1 overflow-y-auto p-5 sm:p-6">
                    <ApercuEleveBody
                        key={gen}
                        question={q}
                        onVariant={regenerate ? handleVariant : null}
                    />
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ApercuEleveModal;
